/**
 * Number Management Center — Version 1 (refined).
 *
 * Philosophy: opening a business bank account, not shopping for telecom.
 *   • Every customer sees "included in your plan" — never provider $/mo.
 *   • Entitlement rule: 1 seat = 1 included business number.
 *   • Wizard flow (6 steps, one decision each):
 *       Country → State/Province → Area Code → Number Type → Choose Number → Assign
 *   • Number Type is Local Business Number or Toll-Free Number (where supported).
 *   • No vanity / pattern / premium / gold / repeating-digit search.
 *   • Provider (Twilio) is an implementation detail, never surfaced in the UI.
 *
 * Backend contract (all provider-agnostic):
 *   GET  /numbers                      — org numbers
 *   GET  /numbers/entitlement          — { included, used, remaining, at_limit }
 *   GET  /numbers/catalog              — countries + supported types
 *   GET  /numbers/search?...           — candidates matching filters
 *   POST /twilio/numbers/buy           — with optional exact phone_number + assign_to
 *   POST /numbers/{id}/assign          — reassign / unassign
 *   DELETE /numbers/{id}               — release
 */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, Trash, Globe, ArrowRight, Check, X, Phone, Sparkle,
  MagnifyingGlass, ArrowLeft, ShieldStar, LockKey,
} from "@phosphor-icons/react";
import "./dashboard-premium.css";

// Assignment targets shown to customers in V1. Order = display order.
const TARGET_TYPES = [
  { key: "unassigned",   label: "Leave Unassigned", desc: "Park this number. Assign it later from this page." },
  { key: "user",         label: "Seat / User",      desc: "Route to a single agent's softphone." },
  { key: "team",         label: "Team",             desc: "Route to every agent on a team." },
  { key: "queue",        label: "Queue",            desc: "Send to a call queue for distribution." },
  { key: "ring_group",   label: "Ring Group",       desc: "Ring a specific group of agents together." },
  { key: "reception",    label: "Reception / Auto-Attendant", desc: "Front desk workflow or IVR menu." },
];

const targetLabel = (key) =>
  TARGET_TYPES.find((t) => t.key === key)?.label
  || (key ? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Unassigned");

// Countries with detailed regions (US + CA). Others use a free-text field.
const STATE_LISTS = {
  US: [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
    "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
    "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
  ],
  CA: [
    "AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT",
  ],
};

// V1 rule — hide anything except local + toll-free from customers.
const V1_NUMBER_TYPES = ["local", "toll-free"];
const humanType = (t) =>
  t === "local" ? "Local Business Number"
  : t === "toll-free" ? "Toll-Free Number"
  : t.replace(/\b\w/g, (c) => c.toUpperCase());

// -----------------------------------------------------------------------------
export default function Numbers() {
  const { user, refresh } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const adminSeatMode = searchParams.get("admin_seat") === "1";
  const [nums, setNums] = useState([]);
  const [entitlement, setEntitlement] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [adminSeatOpen, setAdminSeatOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [n, e] = await Promise.all([
        api.get("/numbers"),
        api.get("/numbers/entitlement"),
      ]);
      setNums(n.data || []);
      setEntitlement(e.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Deep-link ?admin_seat=1 opens the admin-personal-seat purchase wizard.
  useEffect(() => {
    if (adminSeatMode && !user?.has_own_seat) setAdminSeatOpen(true);
  }, [adminSeatMode, user?.has_own_seat]);

  const release = async (id) => {
    if (!window.confirm("Release this number? This cannot be undone.")) return;
    await api.delete(`/numbers/${id}`);
    load();
  };

  const atLimit = !!entitlement?.at_limit;
  const isAdmin = user?.role === "admin";
  const adminNeedsOwnSeat = isAdmin && user && !user.has_own_seat;

  return (
    <div className="hbf-numbers-page" data-testid="numbers-page">
      {/* Hero */}
      <header className="hd-hero">
        <div>
          <div className="hd-hero__eyebrow" data-testid="numbers-eyebrow">Communication Seats</div>
          <h1 className="hd-hero__title">
            <span className="hd-hero__title-accent">Communication Seats</span> for your team
          </h1>
          <p className="hd-hero__sub">
            Every Communication Seat on your plan includes a business number, softphone access,
            and messaging. Activate a new seat in a few clicks — no telecom paperwork.
          </p>
        </div>
        <div className="hd-hero__actions">
          <button
            onClick={() => {
              if (atLimit) {
                // All seats occupied — admin needs to grow their plan first.
                // Deep-link to Settings' Manage Seats card (backed by the
                // existing /billing/seats/preview + /purchase flow).
                window.location.href = "/app/settings#manage-seats";
              } else {
                setWizardOpen(true);
              }
            }}
            className="hd-btn hd-btn--primary"
            title={atLimit ? "All Communication Seats are used. Click to add more seats to your plan first." : undefined}
            data-testid="buy-number-btn"
          >
            <Plus size={14} weight="bold" /> {atLimit ? "Add more seats to plan" : "Add Communication Seat"}
          </button>
        </div>
      </header>

      {err && (
        <div className="hd-notice hd-notice--danger" data-testid="numbers-err">
          <X size={18} weight="bold" /><div>{err}</div>
        </div>
      )}

      {/* Entitlement banner */}
      {entitlement && <EntitlementCard entitlement={entitlement} />}

      {/* Admin-personal-seat CTA — shown when admin has NO seat of their own */}
      {adminNeedsOwnSeat && (
        <div
          className="hd-card"
          style={{
            marginTop: 18, padding: 22, display: "flex", gap: 20,
            alignItems: "center", flexWrap: "wrap",
            background: "linear-gradient(180deg, rgba(37,99,235,.06), rgba(37,99,235,.01))",
            borderColor: "var(--hd-accent-2)",
          }}
          data-testid="admin-seat-banner"
        >
          <div className="hd-num-card__country" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <LockKey size={18} weight="fill" /> ADMIN · PERSONAL COMMS LOCKED
          </div>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontWeight: 700, color: "var(--hd-text)", fontSize: 15 }}>
              Get a Communication Seat for yourself
            </div>
            <div style={{ fontSize: 13.5, color: "var(--hd-text-muted)", marginTop: 4 }}>
              The seats you've bought belong to your staff. To use the dialer, personal SMS, voicemail, recordings and contacts, activate a seat that's assigned to your admin account.
            </div>
          </div>
          <button
            className="hd-btn hd-btn--primary"
            onClick={() => setAdminSeatOpen(true)}
            data-testid="admin-seat-cta"
          >
            <ShieldStar size={14} weight="fill" /> Activate my seat <ArrowRight size={13} weight="bold" />
          </button>
        </div>
      )}

      {/* Numbers grid */}
      <div className="hd-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", marginTop: 20 }} data-testid="numbers-grid">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="hd-card hd-num-card">
              <div className="hd-skel" style={{ height: 12, width: 100 }} />
              <div className="hd-skel" style={{ height: 26, width: 180 }} />
              <div className="hd-skel" style={{ height: 12, width: 140 }} />
            </div>
          ))
        ) : nums.length === 0 ? (
          <div className="hd-card hd-empty" style={{ gridColumn: "1 / -1" }} data-testid="numbers-empty">
            <div className="hd-empty__icon"><Phone size={22} weight="fill" /></div>
            <div style={{ fontWeight: 600, color: "var(--hd-text)", fontSize: 15 }}>No Communication Seats active yet</div>
            <div style={{ marginTop: 6, marginBottom: 16 }}>
              Activate your first Communication Seat to start receiving calls and messages.
            </div>
            <button className="hd-btn hd-btn--primary" onClick={() => setWizardOpen(true)} disabled={atLimit} data-testid="numbers-empty-buy">
              <Plus size={14} weight="bold" /> Activate your first Communication Seat
            </button>
          </div>
        ) : (
          nums.map((n) => (
            <NumberCard
              key={n.id}
              n={n}
              onReassign={() => setReassignTarget(n)}
            />
          ))
        )}
      </div>

      {wizardOpen && (
        <PurchaseWizard
          onClose={() => setWizardOpen(false)}
          onPurchased={() => { setWizardOpen(false); load(); }}
        />
      )}
      {adminSeatOpen && (
        <AdminPersonalSeatWizard
          user={user}
          onClose={() => {
            setAdminSeatOpen(false);
            if (adminSeatMode) {
              const next = new URLSearchParams(searchParams);
              next.delete("admin_seat");
              setSearchParams(next, { replace: true });
            }
          }}
          onPurchased={async () => {
            setAdminSeatOpen(false);
            if (adminSeatMode) {
              const next = new URLSearchParams(searchParams);
              next.delete("admin_seat");
              setSearchParams(next, { replace: true });
            }
            await load();
            try { await refresh(); } catch { /* noop */ }
          }}
        />
      )}
      {reassignTarget && (
        <ReassignDialog
          number={reassignTarget}
          onClose={() => setReassignTarget(null)}
          onSaved={() => { setReassignTarget(null); load(); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function EntitlementCard({ entitlement }) {
  const { included, used, remaining, at_limit } = entitlement;
  const pct = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;
  return (
    <div className="hd-card" style={{ padding: 22, display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }} data-testid="entitlement-card">
      <div style={{ flex: 1, minWidth: 240 }}>
        <div className="hd-panel__eyebrow" style={{ margin: 0 }}>/ COMMUNICATION SEATS</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginTop: 6 }}>
          <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: "-0.02em", fontFeatureSettings: '"tnum"', color: "var(--hd-text)" }} data-testid="entitlement-used">
            {used} <span style={{ fontSize: 22, color: "var(--hd-text-subtle)", fontWeight: 500 }}>of {included} Active</span>
          </div>
        </div>
        <div style={{ height: 8, marginTop: 12, background: "var(--hd-surface-soft)", borderRadius: 999, overflow: "hidden" }}>
          <div
            data-testid="entitlement-bar"
            style={{
              height: "100%",
              width: `${pct}%`,
              background: at_limit
                ? "linear-gradient(90deg, #DC2626, #B91C1C)"
                : "linear-gradient(90deg, #2563EB, #10B981)",
              transition: "width .35s cubic-bezier(.4,0,.2,1)",
            }}
          />
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
        <div style={{ fontSize: 12, color: "var(--hd-text-muted)" }}>Available</div>
        <div
          style={{ fontSize: 30, fontWeight: 700, color: at_limit ? "var(--hd-danger)" : "var(--hd-accent-2)", fontFeatureSettings: '"tnum"' }}
          data-testid="entitlement-remaining"
        >
          {remaining}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function NumberCard({ n, onReassign }) {
  const isAssigned = n.assign_target_type && n.assign_target_type !== "unassigned";
  return (
    <div className="hd-card hd-num-card" data-testid={`number-card-${n.id}`}>
      <div className="hd-num-card__head">
        <div className="hd-num-card__country" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
          <Globe size={12} weight="fill" /> {n.country} · {n.type === "local" ? "LOCAL" : n.type === "toll-free" ? "TOLL-FREE" : (n.type || "").toUpperCase()}
        </div>
        <span
          className={`hd-pill hd-pill--${isAssigned ? "completed" : "no-answer"}`}
          data-testid={`assigned-pill-${n.id}`}
        >
          {isAssigned ? "Assigned" : "Unassigned"}
        </span>
      </div>
      <div className="hd-num-card__number" data-testid={`number-phone-${n.id}`}>
        {n.phone || n.phone_e164 || "—"}
      </div>

      {/* Assigned staff member (or empty-state hint) */}
      <div
        style={{
          marginTop: 10, padding: "10px 12px",
          background: isAssigned ? "var(--hd-bg-subtle,rgba(37,99,235,.06))" : "var(--hd-bg-muted,rgba(148,163,184,.08))",
          border: "1px solid var(--hd-border,rgba(148,163,184,.18))",
          borderRadius: 8,
        }}
        data-testid={`seat-assignee-${n.id}`}
      >
        <div className="hd-panel__eyebrow" style={{ margin: 0, marginBottom: 4 }}>
          Assigned to
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--hd-text)", lineHeight: 1.2 }}>
          {isAssigned ? (n.assign_target_label || "Staff member") : "No staff member yet"}
        </div>
        {isAssigned && n.assign_target_type && n.assign_target_type !== "user" && (
          <div style={{ fontSize: 11, color: "var(--hd-text-muted)", marginTop: 2 }}>
            {targetLabel(n.assign_target_type)}
          </div>
        )}
      </div>

      {n.label && <div className="hd-num-card__label" style={{ marginTop: 8 }}>{n.label}</div>}

      <div className="hd-num-card__meta">
        <span
          className="hd-pill hd-pill--inbound"
          style={{ whiteSpace: "nowrap", flexShrink: 0 }}
          data-testid={`included-pill-${n.id}`}
          title="Numbers are permanent — you can change who they're assigned to any time."
        >
          Communication Seat
        </span>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button
            onClick={onReassign}
            className="hd-btn hd-btn--secondary"
            style={{ padding: "6px 12px", fontSize: 12 }}
            data-testid={`reassign-${n.id}`}
          >
            {isAssigned ? "Edit / Reassign" : "Assign staff"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function PurchaseWizard({ onClose, onPurchased }) {
  const STEPS = [
    { n: 1, label: "Country" },
    { n: 2, label: "State / Province" },
    { n: 3, label: "Area code" },
    { n: 4, label: "Number type" },
    { n: 5, label: "Choose number" },
    { n: 6, label: "Assign" },
  ];

  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState(null);
  const [country, setCountry] = useState(null);
  const [region, setRegion] = useState("");
  const [areaCode, setAreaCode] = useState("");
  const [type, setType] = useState("local");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [assign, setAssign] = useState({ target_type: "unassigned", target_id: "", target_label: "" });
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/numbers/catalog").then(({ data }) => {
      setCatalog(data);
      const us = (data.countries || []).find((c) => c.iso === "US");
      if (us) setCountry(us.iso);
    }).catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
  }, []);

  const countryMeta = useMemo(
    () => catalog?.countries.find((c) => c.iso === country),
    [catalog, country],
  );
  const availableTypes = useMemo(
    () => (countryMeta?.types || []).filter((t) => V1_NUMBER_TYPES.includes(t)),
    [countryMeta],
  );

  // Ensure the type is always valid for the chosen country.
  useEffect(() => {
    if (availableTypes.length > 0 && !availableTypes.includes(type)) {
      setType(availableTypes[0]);
    }
  }, [availableTypes, type]);

  const runSearch = async () => {
    if (!country) return;
    setBusy(true); setErr("");
    try {
      const params = new URLSearchParams({ country, type, limit: "20" });
      if (region)   params.set("region", region);
      if (areaCode) params.set("area_code", areaCode);
      // V1 defaults: voice-capable numbers only (business communication)
      params.set("voice", "true");
      const { data } = await api.get(`/numbers/search?${params.toString()}`);
      setResults(data.results || []);
      setSelected(null);
      setStep(5);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const purchase = async () => {
    if (!selected) return;
    setBusy(true); setErr("");
    try {
      await api.post("/twilio/numbers/buy", {
        country, type,
        phone_number: selected.phone_number,
        label: label || undefined,
        assign_to: assign.target_type === "unassigned"
          ? { target_type: "unassigned" }
          : { target_type: assign.target_type, target_id: assign.target_id || null, target_label: assign.target_label || null },
      });
      onPurchased();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const canAdvance = (s) => {
    if (s === 1) return !!country;
    if (s === 2) return !!region;   // state / region is now REQUIRED — no skip
    if (s === 3) return !!areaCode; // area code is now REQUIRED — no skip
    if (s === 4) return !!type;
    if (s === 5) return !!selected;
    return true;
  };

  const isState23FreeText = !STATE_LISTS[country];

  return (
    <div className="hd-wizard-scrim" data-testid="wizard-scrim" onClick={onClose}>
      <button className="hd-wizard__close" onClick={onClose} data-testid="wizard-close" aria-label="Close">
        <X size={16} weight="bold" />
      </button>
      <div className="hd-wizard" onClick={(e) => e.stopPropagation()} data-testid="purchase-wizard">
        <div className="hd-wizard__head">
          <div className="hd-hero__eyebrow" style={{ marginBottom: 4 }}>
            <Sparkle size={12} weight="fill" /> Activate a Communication Seat
          </div>
          <div className="hd-wizard__title">Activate a new Communication Seat</div>
          <div className="hd-wizard__sub">
            Each Communication Seat includes a business number, softphone access, and messaging.
            This takes about a minute.
          </div>
          <div className="hd-steps" data-testid="wizard-steps">
            {STEPS.map((s, i) => (
              <div key={s.n} style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                <div
                  className={`hd-step ${step === s.n ? "hd-step--active" : ""} ${step > s.n ? "hd-step--done" : ""}`}
                  data-testid={`step-${s.n}`}
                >
                  <span className="hd-step__num">{step > s.n ? <Check size={13} weight="bold" /> : s.n}</span>
                  {s.label}
                </div>
                {i < STEPS.length - 1 && <span className="hd-step__sep" />}
              </div>
            ))}
          </div>
        </div>

        <div className="hd-wizard__body">
          {err && (
            <div className="hd-notice hd-notice--danger" data-testid="wizard-err">
              <X size={16} weight="bold" /><div>{err}</div>
            </div>
          )}

          {/* Step 1 — Country */}
          {step === 1 && (
            <div data-testid="step-1-body">
              <StepHeader
                title="Which country is this number for?"
                sub="Choose where your customers will dial from."
              />
              <div className="hd-country-grid">
                {(catalog?.countries || []).map((c) => (
                  <button
                    key={c.iso}
                    className={`hd-country ${country === c.iso ? "hd-country--active" : ""}`}
                    onClick={() => setCountry(c.iso)}
                    data-testid={`country-${c.iso}`}
                  >
                    <div className="hd-country__flag">{c.iso}</div>
                    <div className="hd-country__body">
                      <div className="hd-country__name">{c.name}</div>
                      <div className="hd-country__dial">{c.dial}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2 — State / Province */}
          {step === 2 && countryMeta && (
            <div data-testid="step-2-body">
              <StepHeader
                title={`Which state or province in ${countryMeta.name}?`}
                sub="Required — choose a region for locally-recognized numbers."
              />
              {STATE_LISTS[country] ? (
                <div className="hd-field" style={{ maxWidth: 320 }}>
                  <label>State / Province</label>
                  <select
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    data-testid="filter-region"
                  >
                    <option value="">Select a state / province…</option>
                    {STATE_LISTS[country].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="hd-field" style={{ maxWidth: 320 }}>
                  <label>State / Province / Region</label>
                  <input
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder="e.g. England, Maharashtra"
                    data-testid="filter-region"
                  />
                  <div className="hd-field__help">Required.</div>
                </div>
              )}
            </div>
          )}

          {/* Step 3 — Area code */}
          {step === 3 && (
            <div data-testid="step-3-body">
              <StepHeader
                title="Which area code?"
                sub="Required — pick a code your customers already recognize."
              />
              <div className="hd-field" style={{ maxWidth: 240 }}>
                <label>Area code</label>
                <input
                  value={areaCode}
                  onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="e.g. 415"
                  inputMode="numeric"
                  data-testid="filter-area-code"
                />
              </div>
            </div>
          )}

          {/* Step 4 — Number type */}
          {step === 4 && countryMeta && (
            <div data-testid="step-4-body">
              <StepHeader
                title="Local or toll-free?"
                sub="Local numbers feel personal to nearby customers. Toll-free numbers are best for national reach."
              />
              <div className="hd-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }} data-testid="type-row">
                {availableTypes.map((t) => (
                  <button
                    key={t}
                    className={`hd-target ${type === t ? "hd-target--active" : ""}`}
                    onClick={() => setType(t)}
                    data-testid={`type-${t}`}
                    style={{ padding: 18 }}
                  >
                    <div className="hd-target__title">{humanType(t)}</div>
                    <div className="hd-target__desc">
                      {t === "local"
                        ? "A number with a local area code — recognizable to customers in the region."
                        : "A toll-free number — free to call from anywhere in the country."}
                    </div>
                  </button>
                ))}
              </div>
              {availableTypes.length < countryMeta.types.length && (
                <div className="hd-field__help" style={{ marginTop: 14 }}>
                  More number categories will unlock in a future release.
                </div>
              )}
            </div>
          )}

          {/* Step 5 — Choose number */}
          {step === 5 && (
            <div data-testid="step-5-body">
              <StepHeader
                title="Choose your number"
                sub={`Available ${humanType(type).toLowerCase()}s${areaCode ? ` starting with ${areaCode}` : ""}${region ? ` in ${region}` : ""}.`}
              />
              {busy && <div className="hd-empty">Searching available numbers…</div>}
              {!busy && results.length === 0 && (
                <div className="hd-empty">
                  <div className="hd-empty__icon"><MagnifyingGlass size={22} weight="fill" /></div>
                  <div style={{ fontWeight: 600, color: "var(--hd-text)" }}>No numbers matched.</div>
                  <div style={{ marginTop: 6 }}>Try broadening your area code or removing the region.</div>
                </div>
              )}
              {!busy && results.length > 0 && (
                <div className="hd-results-grid" data-testid="results-grid">
                  {results.map((r) => (
                    <button
                      key={r.phone_number}
                      className={`hd-result ${selected?.phone_number === r.phone_number ? "hd-result--selected" : ""}`}
                      onClick={() => setSelected(r)}
                      data-testid={`result-${r.phone_number}`}
                    >
                      <div className="hd-result__phone">{r.friendly_name || r.phone_number}</div>
                      <div className="hd-result__meta">
                        <span>
                          {r.iso_country}
                          {r.region ? ` · ${r.region}` : ""}
                          {r.locality ? ` · ${r.locality}` : ""}
                        </span>
                      </div>
                      <div className="hd-result__meta" style={{ marginTop: "auto", fontWeight: 600, color: "var(--hd-accent-2)" }}>
                        Included with your Communication Seat
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 6 — Assign */}
          {step === 6 && selected && (
            <div data-testid="step-6-body">
              <StepHeader
                title="Where should this number route?"
                sub="Assign now or leave it unassigned and route it later — no rush."
              />
              <div className="hd-card" style={{ padding: 16, marginBottom: 16, background: "linear-gradient(180deg, rgba(37,99,235,.04), rgba(37,99,235,.01))" }}>
                <div className="hd-panel__eyebrow">/ SELECTED NUMBER</div>
                <div className="hd-result__phone">{selected.friendly_name || selected.phone_number}</div>
                <div className="hd-result__meta">
                  {humanType(type)}{selected.region ? ` · ${selected.region}` : ""} · Included with your Communication Seat
                </div>
              </div>

              <div className="hd-field" style={{ marginBottom: 18 }}>
                <label>Give it a name (optional)</label>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Sales Line, Support, San Francisco Office"
                  data-testid="assign-label"
                />
              </div>

              <div className="hd-field">
                <label>Route incoming calls to</label>
                <div className="hd-target-grid" data-testid="target-grid">
                  {TARGET_TYPES.map((t) => (
                    <button
                      key={t.key}
                      className={`hd-target ${assign.target_type === t.key ? "hd-target--active" : ""}`}
                      onClick={() => setAssign({ target_type: t.key, target_id: "", target_label: "" })}
                      data-testid={`target-${t.key}`}
                    >
                      <div className="hd-target__title">{t.label}</div>
                      <div className="hd-target__desc">{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {assign.target_type !== "unassigned" && (
                <div className="hd-field" style={{ marginTop: 14, maxWidth: 480 }}>
                  <label>Destination name (optional)</label>
                  <input
                    value={assign.target_label}
                    onChange={(e) => setAssign((a) => ({ ...a, target_label: e.target.value }))}
                    placeholder={`e.g. ${assign.target_type === "user" ? "Alice Chen" : assign.target_type === "team" ? "Sales Team" : "Front Desk"}`}
                    data-testid="target-label"
                  />
                  <div className="hd-field__help">You can wire the exact destination from the Team &amp; Routing settings.</div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="hd-wizard__foot">
          <button
            className="hd-btn hd-btn--ghost"
            onClick={step === 1 ? onClose : () => setStep((s) => s - 1)}
            disabled={busy}
            data-testid="wizard-back"
          >
            {step === 1 ? "Cancel" : (<><ArrowLeft size={13} weight="bold" /> Back</>)}
          </button>
          <div style={{ fontSize: 12, color: "var(--hd-text-subtle)" }}>Step {step} of 6</div>
          {step < 4 && (
            <button
              className="hd-btn hd-btn--primary"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canAdvance(step)}
              data-testid="wizard-next"
            >
              Continue <ArrowRight size={13} weight="bold" />
            </button>
          )}
          {step === 4 && (
            <button
              className="hd-btn hd-btn--primary"
              onClick={runSearch}
              disabled={busy || !type}
              data-testid="wizard-search"
            >
              {busy ? "Searching…" : "Find available numbers"} <ArrowRight size={13} weight="bold" />
            </button>
          )}
          {step === 5 && (
            <button
              className="hd-btn hd-btn--primary"
              onClick={() => setStep(6)}
              disabled={!selected}
              data-testid="wizard-select-next"
            >
              Continue <ArrowRight size={13} weight="bold" />
            </button>
          )}
          {step === 6 && (
            <button
              className="hd-btn hd-btn--success"
              onClick={purchase}
              disabled={busy || !selected}
              data-testid="wizard-purchase"
            >
              {busy ? "Activating…" : "Activate Communication Seat"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function StepHeader({ title, sub }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.01em", color: "var(--hd-text)" }}>{title}</div>
      {sub && <div style={{ fontSize: 13.5, color: "var(--hd-text-muted)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// ReassignDialog — simple two-mode editor for a Communication Seat.
//
// A number is permanent. Only the staff member assigned to it is mutable.
// This dialog exposes exactly two actions:
//
//   1. Edit the assigned staff member's username / password / display name
//      (the seat stays with the same user, credentials update in place)
//   2. Reassign the seat to a brand-new staff member (creates fresh
//      credentials and removes the previous user's access to this line)
//
// Older routing modes (Team/Queue/Ring Group/Reception) live under separate
// admin surfaces and were removed here per Feb 2026 owner spec — admins ring
// teams from Team Chat rather than at the number level.
// -----------------------------------------------------------------------------
function ReassignDialog({ number, onClose, onSaved }) {
  const isAssigned = !!number.assign_target_id && number.assign_target_type !== "unassigned";
  const [mode, setMode] = useState(isAssigned ? "edit" : "new"); // 'edit' | 'new'
  const [form, setForm] = useState({
    username: number.assign_target_label || "",
    password: "",
    display_name: "",
    email: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const canSubmit = (() => {
    if (mode === "edit") {
      // At least one field must be provided.
      return (form.username && form.username.trim().length >= 2) ||
             (form.password && form.password.length >= 6) ||
             (form.display_name && form.display_name.trim().length > 0);
    }
    // 'new' — username + password are required.
    return (form.username || "").trim().length >= 2 && (form.password || "").length >= 6;
  })();

  async function submit() {
    setBusy(true); setErr("");
    try {
      if (mode === "edit" && isAssigned) {
        // Update the existing assigned user's credentials in place.
        await api.patch(`/numbers/${number.id}/assignee`, {
          username: form.username?.trim() || null,
          password: form.password || null,
          display_name: form.display_name?.trim() || null,
        });
      } else {
        // Reassign to a brand-new staff member.
        await api.post(`/numbers/${number.id}/reassign-new-user`, {
          username: form.username.trim(),
          password: form.password,
          display_name: form.display_name?.trim() || null,
          email: form.email?.trim() || null,
        });
      }
      onSaved();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="hd-wizard-scrim" onClick={onClose} data-testid="reassign-scrim">
      <button className="hd-wizard__close" onClick={onClose} aria-label="Close" data-testid="reassign-close">
        <X size={16} weight="bold" />
      </button>
      <div className="hd-wizard" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()} data-testid="reassign-dialog">
        <div className="hd-wizard__head">
          <div className="hd-hero__eyebrow" style={{ marginBottom: 4 }}>
            <Phone size={12} weight="fill" /> {number.phone}
          </div>
          <div className="hd-wizard__title" style={{ fontSize: 24 }}>
            {isAssigned ? "Edit or reassign staff" : "Assign staff to this number"}
          </div>
          <div className="hd-wizard__sub">
            {isAssigned
              ? <>Currently assigned to <b>{number.assign_target_label || "a staff member"}</b>. The number is permanent — only who uses it can change.</>
              : "Create a staff login for this Communication Seat."}
          </div>
        </div>

        <div className="hd-wizard__body">
          {isAssigned && (
            <div className="hd-seg" role="tablist" style={{ marginBottom: 18, display: "inline-flex", gap: 4, border: "1px solid var(--hd-border,rgba(148,163,184,.2))", padding: 4, borderRadius: 8 }}>
              <button
                onClick={() => setMode("edit")}
                className={`hd-btn ${mode === "edit" ? "hd-btn--primary" : "hd-btn--ghost"}`}
                style={{ padding: "6px 14px", fontSize: 13 }}
                data-testid="reassign-mode-edit"
              >
                Edit username / password
              </button>
              <button
                onClick={() => setMode("new")}
                className={`hd-btn ${mode === "new" ? "hd-btn--primary" : "hd-btn--ghost"}`}
                style={{ padding: "6px 14px", fontSize: 13 }}
                data-testid="reassign-mode-new"
              >
                Assign to a new staff member
              </button>
            </div>
          )}

          {err && (
            <div className="hd-notice hd-notice--danger" data-testid="reassign-err">
              <X size={16} weight="bold" /><div>{err}</div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, maxWidth: 560 }}>
            <div className="hd-field">
              <label>Username{mode === "new" && <span style={{ color: "var(--hd-danger,#dc2626)" }}> *</span>}</label>
              <input
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                placeholder={mode === "edit" ? "Leave blank to keep current" : "e.g. sarah_chen"}
                data-testid="reassign-username"
              />
            </div>
            <div className="hd-field">
              <label>Password{mode === "new" && <span style={{ color: "var(--hd-danger,#dc2626)" }}> *</span>} <span style={{ fontSize: 11, color: "var(--hd-text-muted)" }}>(min 6)</span></label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder={mode === "edit" ? "Leave blank to keep current" : "••••••"}
                data-testid="reassign-password"
              />
            </div>
            <div className="hd-field">
              <label>Display name (optional)</label>
              <input
                value={form.display_name}
                onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
                placeholder="e.g. Sarah Chen"
                data-testid="reassign-display_name"
              />
            </div>
            {mode === "new" && (
              <div className="hd-field">
                <label>Email (optional — sends invite)</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="sarah@company.com"
                  data-testid="reassign-email"
                />
              </div>
            )}
          </div>
        </div>

        <div className="hd-wizard__foot">
          <button className="hd-btn hd-btn--ghost" onClick={onClose} disabled={busy} data-testid="reassign-cancel">
            Cancel
          </button>
          <div />
          <button
            className="hd-btn hd-btn--primary"
            onClick={submit}
            disabled={busy || !canSubmit}
            data-testid="reassign-save"
          >
            {busy
              ? "Saving…"
              : mode === "edit"
                ? "Save changes"
                : "Assign staff member"}
          </button>
        </div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// AdminPersonalSeatWizard — the admin buys a seat for THEMSELVES.
//
// Flow (admin already has payment & user identity):
//   Country → State/Region → Area code → pick number → auto-assign to admin.
// The purchased number is bound and assigned to the admin's user_id in one
// step (no username/password creation — the admin already has a login).
// -----------------------------------------------------------------------------
function AdminPersonalSeatWizard({ user, onClose, onPurchased }) {
  const STEPS = [
    { n: 1, label: "Country" },
    { n: 2, label: "State / Region" },
    { n: 3, label: "Area code" },
    { n: 4, label: "Choose number" },
  ];
  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState(null);
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [areaCode, setAreaCode] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/numbers/catalog")
      .then(({ data }) => setCatalog(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
  }, []);

  const canAdvance = (s) => {
    if (s === 1) return !!country;
    if (s === 2) return !!region;
    if (s === 3) return !!areaCode;
    if (s === 4) return !!selected;
    return true;
  };

  const search = async () => {
    setBusy(true); setErr(""); setResults([]); setSelected(null);
    try {
      const params = new URLSearchParams({
        country, type: "local", limit: "20",
        region, area_code: areaCode, voice: "true",
      });
      const { data } = await api.get(`/numbers/search?${params.toString()}`);
      setResults(data.results || []);
      setStep(4);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const activate = async () => {
    if (!selected || !user?.id) return;
    setBusy(true); setErr("");
    try {
      // Reuse the existing Twilio purchase-and-assign primitive. This binds
      // the number and assigns it to the admin's own user record atomically.
      await api.post("/twilio/numbers/buy", {
        country, type: "local",
        phone_number: selected.phone_number,
        label: "Admin personal line",
        assign_to: {
          target_type: "user",
          target_id: user.id,
          target_label: user.name || user.username || user.email || "Admin",
        },
      });
      onPurchased();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="hd-wizard-scrim" onClick={onClose} data-testid="admin-seat-scrim">
      <button
        className="hd-wizard__close" onClick={onClose}
        data-testid="admin-seat-close" aria-label="Close"
      >
        <X size={16} weight="bold" />
      </button>
      <div
        className="hd-wizard" onClick={(e) => e.stopPropagation()}
        data-testid="admin-seat-wizard"
      >
        <div className="hd-wizard__head">
          <div className="hd-hero__eyebrow" style={{ marginBottom: 4 }}>
            <ShieldStar size={12} weight="fill" /> Activate your personal Communication Seat
          </div>
          <div className="hd-wizard__title">Get a business number for yourself</div>
          <div className="hd-wizard__sub">
            Unlocks the Dialer, personal SMS, inbox, voicemail, recordings and contacts. The number is assigned to your admin account automatically.
          </div>
          <div className="hd-steps" data-testid="admin-seat-steps">
            {STEPS.map((s, i) => (
              <div key={s.n} style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                <div
                  className={`hd-step ${step === s.n ? "hd-step--active" : ""} ${step > s.n ? "hd-step--done" : ""}`}
                  data-testid={`admin-seat-step-${s.n}`}
                >
                  <span className="hd-step__num">{step > s.n ? <Check size={13} weight="bold" /> : s.n}</span>
                  {s.label}
                </div>
                {i < STEPS.length - 1 && <span className="hd-step__sep" />}
              </div>
            ))}
          </div>
        </div>

        <div className="hd-wizard__body">
          {err && (
            <div className="hd-notice hd-notice--danger" data-testid="admin-seat-err">
              <X size={16} weight="bold" /><div>{err}</div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="admin-seat-step-1-body">
              <StepHeader
                title="Which country is your number for?"
                sub="Choose where your customers will dial from."
              />
              <div className="hd-country-grid">
                {(catalog?.countries || []).map((c) => (
                  <button
                    key={c.iso}
                    className={`hd-country ${country === c.iso ? "hd-country--active" : ""}`}
                    onClick={() => { setCountry(c.iso); setRegion(""); }}
                    data-testid={`admin-seat-country-${c.iso}`}
                  >
                    <div className="hd-country__flag">{c.iso}</div>
                    <div className="hd-country__body">
                      <div className="hd-country__name">{c.name}</div>
                      <div className="hd-country__dial">{c.dial}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="admin-seat-step-2-body">
              <StepHeader
                title="Which state or region?"
                sub="Required — choose a region for locally-recognized numbers."
              />
              {STATE_LISTS[country] ? (
                <div className="hd-field" style={{ maxWidth: 320 }}>
                  <label>State / Region</label>
                  <select
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    data-testid="admin-seat-region"
                  >
                    <option value="">Select a state / region…</option>
                    {STATE_LISTS[country].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="hd-field" style={{ maxWidth: 320 }}>
                  <label>State / Region</label>
                  <input
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder="e.g. England, Maharashtra"
                    data-testid="admin-seat-region"
                  />
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="admin-seat-step-3-body">
              <StepHeader
                title="Which area code?"
                sub="Required — pick a code your customers already recognize."
              />
              <div className="hd-field" style={{ maxWidth: 240 }}>
                <label>Area code</label>
                <input
                  value={areaCode}
                  onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="e.g. 415"
                  inputMode="numeric"
                  data-testid="admin-seat-area-code"
                />
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="admin-seat-step-4-body">
              <StepHeader
                title="Choose your number"
                sub={`Available local numbers${areaCode ? ` starting with ${areaCode}` : ""}${region ? ` in ${region}` : ""}.`}
              />
              {busy && <div className="hd-empty">Searching…</div>}
              {!busy && results.length === 0 && (
                <div className="hd-empty">
                  <div className="hd-empty__icon"><MagnifyingGlass size={22} weight="fill" /></div>
                  <div style={{ fontWeight: 600, color: "var(--hd-text)" }}>No numbers matched.</div>
                  <div style={{ marginTop: 6 }}>Try broadening your area code.</div>
                </div>
              )}
              {!busy && results.length > 0 && (
                <div className="hd-results-grid" data-testid="admin-seat-results">
                  {results.map((r) => (
                    <button
                      key={r.phone_number}
                      className={`hd-result ${selected?.phone_number === r.phone_number ? "hd-result--selected" : ""}`}
                      onClick={() => setSelected(r)}
                      data-testid={`admin-seat-result-${r.phone_number}`}
                    >
                      <div className="hd-result__phone">{r.friendly_name || r.phone_number}</div>
                      <div className="hd-result__meta">
                        <span>
                          {r.iso_country}
                          {r.region ? ` · ${r.region}` : ""}
                          {r.locality ? ` · ${r.locality}` : ""}
                        </span>
                      </div>
                      <div className="hd-result__meta" style={{ marginTop: "auto", fontWeight: 600, color: "var(--hd-accent-2)" }}>
                        Auto-assigned to you on activation
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="hd-wizard__foot">
          <button
            className="hd-btn hd-btn--ghost"
            onClick={step === 1 ? onClose : () => setStep((s) => s - 1)}
            disabled={busy}
            data-testid="admin-seat-back"
          >
            {step === 1 ? "Cancel" : (<><ArrowLeft size={13} weight="bold" /> Back</>)}
          </button>
          <div style={{ fontSize: 12, color: "var(--hd-text-subtle)" }}>Step {step} of 4</div>
          {step < 3 && (
            <button
              className="hd-btn hd-btn--primary"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canAdvance(step)}
              data-testid="admin-seat-next"
            >
              Continue <ArrowRight size={13} weight="bold" />
            </button>
          )}
          {step === 3 && (
            <button
              className="hd-btn hd-btn--primary"
              onClick={search}
              disabled={!canAdvance(3) || busy}
              data-testid="admin-seat-search"
            >
              {busy ? "Searching…" : "Find available numbers"} <ArrowRight size={13} weight="bold" />
            </button>
          )}
          {step === 4 && (
            <button
              className="hd-btn hd-btn--success"
              onClick={activate}
              disabled={!selected || busy}
              data-testid="admin-seat-activate"
            >
              {busy ? "Activating…" : "Activate my seat"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
